import { promiseState, useBooleanState } from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useState } from 'react';
import { viewTexts } from '../../../constants/view-texts';
import { useListFiltered } from '../../../functions/hooks/use-list-filtered';
import { userList$ } from '../../../global-state/database/user-list';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IUserType } from '../../../types/record/user';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { UserListView } from './user-list-view';

export const UserList: FC<{}> = memo(() => {
  const [
    createNewUserDialogIsOpen,
    openCreateNewUserDialog,
    closeCreateNewUserDialog
  ] = useBooleanState(false);

  const [
    editUserDialogIsOpen,
    openEditUserDialog,
    closeEditUserDialog
  ] = useBooleanState(false);

  const [
    deleteUserDialogIsOpen,
    openDeleteUserDialog,
    closeDeleteUserDialog
  ] = useBooleanState(false);

  const [
    // dummy comment to control prettier
    userToEdit,
    setUserToEdit
  ] = useState<IUserType | undefined>(undefined);

  const [
    // dummy comment to control prettier
    userToDelete,
    setUserToDelete
  ] = useState<IUserType | undefined>(undefined);

  /* event handlers */

  const editUserButtonOnClick = useCallback(
    (user: IUserType) => {
      setUserToEdit(user);
      openEditUserDialog();
    },
    [setUserToEdit, openEditUserDialog]
  );

  const deleteUserButtonOnClick = useCallback(
    (user: IUserType) => {
      setUserToDelete(user);
      openDeleteUserDialog();
    },
    [setUserToDelete, openDeleteUserDialog]
  );

  const {
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    setRowsPerPage: onRowsPerPageChange,
    numRows,
    listFilteredCurrentPage: userListFiltered
  } = useListFiltered(userList$);

  useLoadingPage(userListFiltered?.status === 'pending');

  return (
    <>
      {userListFiltered === undefined ? (
        undefined
      ) : userListFiltered.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.userList} />
      ) : (
        <UserListView
          searchString={searchString}
          onSearchStringChange={onSearchStringChange}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          numRows={numRows}
          userListFiltered={userListFiltered.value.latest}
          createNewUserOnClick={openCreateNewUserDialog}
          createNewUserDialogOpen={createNewUserDialogIsOpen}
          createNewUserDialogOnClose={closeCreateNewUserDialog}
          editUserOnClick={editUserButtonOnClick}
          editUserDialogIsOpen={editUserDialogIsOpen}
          editUserDialogOnClose={closeEditUserDialog}
          userToEdit={userToEdit}
          deleteUserOnClick={deleteUserButtonOnClick}
          deleteUserDialogIsOpen={deleteUserDialogIsOpen}
          deleteUserDialogOnClose={closeDeleteUserDialog}
          userToDelete={userToDelete}
        />
      )}
    </>
  );
});

UserList.displayName = 'UserList';
